import { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import {
  Tabs,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  UserPlus,
  Trash,
  Pencil,
  Users,
  User,
  Folder,
  Settings,
  ChevronRight,
  FolderPlus,
  Eye,
} from "lucide-react";

import { candidatesAPI2, candidateFoldersAPI, settingsAPI } from "@/services/api";
import { useToast } from "@/hooks/use-toast";

import AddCandidateModal from "@/components/admin/AddCandidateModal";
import EditCandidateModal from "@/components/admin/EditCandidateModal";
import CreateFolderModal from "@/components/admin/CreateFolderModal";
import FolderManagementModal from "@/components/admin/FolderManagementModal";
import ModernPageTemplate, { 
  ModernBadge, 
  ModernLoading,
  ModernPaginationFooter
} from "@/components/common/ModernPageTemplate";

import { format } from "date-fns";

const FolderView = ({
  folderGroups = [],
  title,
  icon,
  folderPagination = { totalPages: 1, currentPage: 1 },
  folderPage,
  setFolderPage,
  itemsPerPage = 2,
  isMyCandidate = true,
  refetchFolders,
  refetchCandidates,
}) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [folderCandidates, setFolderCandidates] = useState([]);
  const [folderCandidatesPagination, setFolderCandidatesPagination] = useState({
    totalPages: 1,
    currentPage: 1,
    totalItems: 0,
  });
  const [isLoadingCandidates, setIsLoadingCandidates] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [localPopupPage, setLocalPopupPage] = useState(1);
  const { toast } = useToast();

  const handleOpenCandidates = async (folder) => {
    setSelectedFolder(folder);
    setSearchTerm("");
    setLocalPopupPage(1);
    setIsLoadingCandidates(true);

    try {
      const response = await candidatesAPI2.getMyFolderCandidates({
        folderId: folder._id || folder.id,
        page: 1,
        limit: itemsPerPage,
        isMyCandidate,
      });

      const candidates = response.data?.data?.candidates || [];
      const pagination = response.data?.data?.pagination || {
        totalPages: 1,
        currentPage: 1,
        totalItems: 0,
      };

      setFolderCandidates(candidates);
      setFolderCandidatesPagination({
        totalPages: pagination.pages || pagination.totalPages || 1,
        currentPage: pagination.page || 1,
        totalItems: pagination.total || 0,
      });
      setIsDialogOpen(true);
    } catch (error) {
      console.error("Error fetching folder candidates:", error);
      toast({
        title: "Error",
        description: "Failed to load folder candidates",
        variant: "destructive",
      });
    } finally {
      setIsLoadingCandidates(false);
    }
  };

  // Real-time search handler - triggers on every keystroke
  const handleSearchFolderCandidates = async (searchValue) => {
    if (!selectedFolder) return;

    setIsLoadingCandidates(true);
    try {
      const response = await candidatesAPI2.getMyFolderCandidates({
        folderId: selectedFolder._id || selectedFolder.id,
        page: 1,
        limit: itemsPerPage,
        search: searchValue,
        isMyCandidate,
      });

      const candidates = response.data?.data?.candidates || [];
      const pagination = response.data?.data?.pagination || {
        totalPages: 1,
        currentPage: 1,
        totalItems: 0,
      };

      setFolderCandidates(candidates);
      setFolderCandidatesPagination({
        totalPages: pagination.pages || pagination.totalPages || 1,
        currentPage: 1,
        totalItems: pagination.total || pagination.totalItems || 0,
      });
      setLocalPopupPage(1);
    } catch (error) {
      console.error("Error searching folder candidates:", error);
      toast({
        title: "Error",
        description: "Failed to search candidates",
        variant: "destructive",
      });
    } finally {
      setIsLoadingCandidates(false);
    }
  };

  const handleFolderPageChange = async (newPage) => {
    if (!selectedFolder) return;

    setIsLoadingCandidates(true);
    try {
      const response = await candidatesAPI2.getMyFolderCandidates({
        folderId: selectedFolder._id || selectedFolder.id,
        page: newPage,
        limit: itemsPerPage,
        search: searchTerm,
        isMyCandidate,
      });

      const candidates = response.data?.data?.candidates || [];
      const pagination = response.data?.data?.pagination || {
        totalPages: 1,
        currentPage: newPage,
        totalItems: 0,
      };

      setFolderCandidates(candidates);
      setFolderCandidatesPagination({
        totalPages: pagination.pages || pagination.totalPages || 1,
        currentPage: newPage,
        totalItems: pagination.total || pagination.totalItems || 0,
      });
      setLocalPopupPage(newPage);
    } catch (error) {
      console.error("Error changing folder candidates page:", error);
      toast({
        title: "Error",
        description: "Failed to load page",
        variant: "destructive",
      });
    } finally {
      setIsLoadingCandidates(false);
    }
  };

  const handleDeleteCandidate = async (candidateId) => {
    if (!confirm("Are you sure you want to delete this candidate?")) return;

    try {
      // Delete API call
      await candidatesAPI2.delete(candidateId);

      toast({
        title: "Success",
        description: "Candidate deleted successfully",
        variant: "default",
      });

      // Refresh folders list (main screen)
      refetchFolders();
      
      // Refresh main candidate lists
      if (refetchCandidates) refetchCandidates();

      // Refresh candidates in the folder
      const response = await candidatesAPI2.getMyFolderCandidates({
        folderId: selectedFolder._id || selectedFolder.id,
        page: localPopupPage,
        limit: itemsPerPage,
        search: searchTerm,
        isMyCandidate,
      });

      const candidates = response.data?.data?.candidates || [];
      const pagination = response.data?.data?.pagination || {
        totalPages: 1,
        currentPage: localPopupPage,
        totalItems: 0,
      };

      setFolderCandidates(candidates);
      setFolderCandidatesPagination(pagination);

      // If there are no candidates left, close the popup
      if (candidates.length === 0) {
        setIsDialogOpen(false);
        return;
      }

      // If we deleted the last item on the page and it's not the first page, move to the previous page
      if (candidates.length === 0 && localPopupPage > 1) {
        handleFolderPageChange(localPopupPage - 1);
      }
    } catch (error) {
      console.error("Error deleting candidate:", error);
      toast({
        title: "Error",
        description:
          error.response?.data?.message || "Failed to delete candidate",
        variant: "destructive",
      });
    }
  };

  const handleEditCandidate = (candidate) => {
    setSelectedCandidate(candidate);
    setIsEditModalOpen(true);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2 mb-4">
        {icon}
        <h3 className="text-lg font-semibold">{title}</h3>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
        {folderGroups.map((folder) => (
          <Card
            key={folder.id || folder._id}
            className="hover:shadow-md transition-shadow cursor-pointer"
          >
            <CardContent className="p-4">
              <div className="flex flex-col items-center space-y-3">
                <div className="relative">
                  <Folder
                    className="h-16 w-16"
                    style={{ color: folder.color || "#3882F6" }}
                  />
                  {(folder.totalCandidates || folder.candidates?.length) >
                    0 && (
                    <div className="absolute -top-1 -right-1 bg-blue-500 text-white text-xs rounded-full w-6 h-6 flex items-center justify-center">
                      {folder.totalCandidates || folder.candidates?.length || 0}
                    </div>
                  )}
                </div>

                <div className="text-center w-full">
                  <h4
                    className="font-medium text-sm truncate"
                    title={folder.name}
                  >
                    {folder.name || "Unnamed Folder"}
                  </h4>
                  {folder.description && (
                    <p
                      className="text-xs text-gray-500 truncate mt-1"
                      title={folder.description}
                    >
                      {folder.description}
                    </p>
                  )}
                  <p className="text-xs text-gray-400 mt-1">
                    {folder.totalCandidates || folder.candidates?.length || 0}{" "}
                    candidate
                    {(folder.totalCandidates !== 1 ||
                      folder.candidates?.length !== 1) &&
                      "s"}
                  </p>
                </div>

                {(folder.totalCandidates || folder.candidates?.length) > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="w-full"
                    onClick={() => handleOpenCandidates(folder)}
                    disabled={isLoadingCandidates}
                  >
                    {isLoadingCandidates &&
                    selectedFolder?._id === folder._id ? (
                      "Loading..."
                    ) : (
                      <>
                        Show Candidates
                        <ChevronRight className="ml-2 h-3 w-3" />
                      </>
                    )}
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {folderPagination.totalPages > 1 && (
        <div className="flex justify-center">
          <Pagination>
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  onClick={() => setFolderPage((p) => Math.max(p - 1, 1))}
                  disabled={folderPage === 1}
                  className={
                    folderPage === 1
                      ? "pointer-events-none opacity-50"
                      : "cursor-pointer"
                  }
                />
              </PaginationItem>
              {Array.from(
                { length: folderPagination.totalPages },
                (_, i) => i + 1
              ).map((page) => (
                <PaginationItem key={page}>
                  <PaginationLink
                    isActive={folderPage === page}
                    onClick={() => setFolderPage(page)}
                    className={`cursor-pointer ${
                      folderPage === page
                        ? "bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground"
                        : ""
                    }`}
                  >
                    {page}
                  </PaginationLink>
                </PaginationItem>
              ))}
              <PaginationItem>
                <PaginationNext
                  onClick={() =>
                    setFolderPage((p) =>
                      Math.min(p + 1, folderPagination.totalPages)
                    )
                  }
                  disabled={folderPage === folderPagination.totalPages}
                  className={
                    folderPage === folderPagination.totalPages
                      ? "pointer-events-none opacity-50"
                      : "cursor-pointer"
                  }
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        </div>
      )}

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>
              {selectedFolder?.name || "Folder"} - Candidates
            </DialogTitle>
          </DialogHeader>

          <div className="flex justify-between items-center mb-4">
            <div className="relative flex-1">
              <Input
                placeholder="Search candidates..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  handleSearchFolderCandidates(e.target.value);
                }}
                className="w-full"
              />
            </div>
          </div>

          <div className="overflow-x-auto">
            {isLoadingCandidates ? (
              <ModernLoading text="Loading candidates..." />
            ) : folderCandidates.length > 0 ? (
              <>
                <div className="admin-table-container">
                  <table className="admin-table">
                    <thead className="admin-table-header">
                      <tr>
                        <th className="admin-table-cell">S.No</th>
                        <th className="admin-table-cell">Name</th>
                        <th className="admin-table-cell">Email</th>
                        <th className="admin-table-cell">Mobile</th>
                        <th className="admin-table-cell">Created At</th>
                        <th className="admin-table-cell text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {folderCandidates.map((candidate, index) => (
                        <tr key={candidate._id || index} className="admin-table-row">
                          <td className="admin-table-cell number">
                            {(localPopupPage - 1) * itemsPerPage + index + 1}
                          </td>
                          <td className="admin-table-cell font-medium">
                            <div className="flex items-center">
                              <User className="w-3 h-3 mr-1 text-gray-400 flex-shrink-0" />
                              <span className="text-sm">{candidate.name || "N/A"}</span>
                            </div>
                          </td>
                          <td className="admin-table-cell">
                            <span className="text-sm">{candidate.email || "N/A"}</span>
                          </td>
                          <td className="admin-table-cell">
                            <span className="text-sm">{candidate.mobileNumber || "N/A"}</span>
                          </td>
                          <td className="admin-table-cell">
                            <span className="text-xs">
                              {format(new Date(candidate.createdAt), "MMM dd, yyyy")}
                            </span>
                          </td>
                          <td className="admin-table-cell text-right">
                            <div className="admin-table-actions">
                              <button
                                className="admin-table-action-btn edit"
                                onClick={() => handleEditCandidate(candidate)}
                                title="Edit candidate"
                              >
                                <Pencil className="h-3 w-3" />
                              </button>
                              <button
                                className="admin-table-action-btn delete"
                                onClick={() => handleDeleteCandidate(candidate._id || candidate.id)}
                                title="Delete candidate"
                              >
                                <Trash className="h-3 w-3" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {folderCandidatesPagination.totalPages > 1 && (
                  <div className="flex justify-center mt-4">
                    <Pagination>
                      <PaginationContent>
                        <PaginationItem>
                          <PaginationPrevious
                            onClick={() =>
                              handleFolderPageChange(localPopupPage - 1)
                            }
                            disabled={localPopupPage === 1}
                            className={
                              localPopupPage === 1
                                ? "pointer-events-none opacity-50"
                                : "cursor-pointer"
                            }
                          />
                        </PaginationItem>
                        {Array.from(
                          {
                            length: folderCandidatesPagination.totalPages,
                          },
                          (_, i) => i + 1
                        ).map((page) => (
                          <PaginationItem key={page}>
                            <PaginationLink
                              isActive={localPopupPage === page}
                              onClick={() => handleFolderPageChange(page)}
                              className={`cursor-pointer ${
                                localPopupPage === page
                                  ? "bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground"
                                  : ""
                              }`}
                            >
                              {page}
                            </PaginationLink>
                          </PaginationItem>
                        ))}
                        <PaginationItem>
                          <PaginationNext
                            onClick={() =>
                              handleFolderPageChange(localPopupPage + 1)
                            }
                            disabled={
                              localPopupPage ===
                              folderCandidatesPagination.totalPages
                            }
                            className={
                              localPopupPage ===
                              folderCandidatesPagination.totalPages
                                ? "pointer-events-none opacity-50"
                                : "cursor-pointer"
                            }
                          />
                        </PaginationItem>
                      </PaginationContent>
                    </Pagination>
                  </div>
                )}
              </>
            ) : (
              <p className="text-center text-gray-500">
                {searchTerm
                  ? "No candidates match your search."
                  : "No candidates found in this folder."}
              </p>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {isEditModalOpen && (
        <EditCandidateModal
          isOpen={isEditModalOpen}
          onClose={() => {
            setIsEditModalOpen(false);
            setSelectedCandidate(null);
          }}
          candidateId={selectedCandidate.id}
          onSuccess={() => {
            handleFolderPageChange(localPopupPage);
            setIsEditModalOpen(false);
            refetchFolders();
            if (refetchCandidates) refetchCandidates();
          }}
        />
      )}
    </div>
  );
};

const Candidates = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  
  // Constants
  const PAGE_LIMIT = 5;
  const FOLDERS_PER_PAGE = 10;
  
  // State management
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState("my");
  const [currentPage, setCurrentPage] = useState(1);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isFolderModalOpen, setIsFolderModalOpen] = useState(false);
  const [isFolderManagementOpen, setIsFolderManagementOpen] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [editingFolder, setEditingFolder] = useState(null);
  const [folderModalRefetchCallback, setFolderModalRefetchCallback] = useState(null);
  
  // Pagination state
  const [rowsPerPage, setRowsPerPage] = useState(FOLDERS_PER_PAGE);
  const [isPaginationEnabled, setIsPaginationEnabled] = useState(true);
  
  // For viewing candidates in a folder
  const [isViewCandidatesOpen, setIsViewCandidatesOpen] = useState(false);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [folderCandidates, setFolderCandidates] = useState([]);
  const [folderCandidatesPagination, setFolderCandidatesPagination] = useState({
    totalPages: 1,
    currentPage: 1,
    totalItems: 0,
  });
  const [isLoadingFolderCandidates, setIsLoadingFolderCandidates] = useState(false);
  const [candidateSearchTerm, setCandidateSearchTerm] = useState("");
  const [candidatePage, setCandidatePage] = useState(1);
  
  // For delete confirmation
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [candidateToDelete, setCandidateToDelete] = useState(null);

  // Effect to adjust footer position based on sidebar state
  useEffect(() => {
    const adjustFooterPosition = () => {
      const footer = document.querySelector('.admin-fixed-footer');
      if (!footer) return;

      if (window.innerWidth >= 1024) {
        const mainContent = document.querySelector('[class*="lg:pl-"]');
        if (mainContent) {
          const classes = mainContent.className;
          if (classes.includes('lg:pl-14')) {
            footer.style.left = '68px';
          } else if (classes.includes('lg:pl-64')) {
            footer.style.left = '256px';
          }
        }
      } else {
        footer.style.left = '0px';
      }
    };

    adjustFooterPosition();
    window.addEventListener('resize', adjustFooterPosition);

    const observer = new MutationObserver(adjustFooterPosition);
    const mainContent = document.querySelector('[class*="lg:pl-"]');
    if (mainContent) {
      observer.observe(mainContent, { 
        attributes: true, 
        attributeFilter: ['class'] 
      });
    }

    return () => {
      window.removeEventListener('resize', adjustFooterPosition);
      observer.disconnect();
    };
  }, []);

  // Query for candidates based on active tab
  const {
    data: candidatesData,
    isLoading: candidatesLoading,
    isError: candidatesError,
    refetch: refetchCandidates,
  } = useQuery({
    queryKey: ["candidates", currentPage, searchTerm, activeTab, rowsPerPage, isPaginationEnabled],
    queryFn: async () => {
      try {
        const params = {
          page: isPaginationEnabled ? currentPage : 1,
          limit: isPaginationEnabled ? rowsPerPage : 999999, // Large number to get all records
          search: searchTerm,
        };
        
        const response = activeTab === "my" 
          ? await candidatesAPI2.getMy(params)
          : await candidatesAPI2.getCompany(params);
          
        return response.data || {
          data: [],
          pagination: {
            totalPages: 1,
            currentPage: 1,
            totalItems: 0,
            itemsPerPage: rowsPerPage,
          },
          totalCandidates: 0,
        };
      } catch (error) {
        console.error("Candidates Error:", error);
        return {
          data: [],
          pagination: {
            totalPages: 1,
            currentPage: 1,
            totalItems: 0,
            itemsPerPage: rowsPerPage,
          },
          totalCandidates: 0,
        };
      }
    },
  });

  const { data: folderData, refetch: refetchFolders } = useQuery({
    queryKey: ["folders-filter"],
    queryFn: () => candidateFoldersAPI.getMy({ limit: 100 }),
  });

  // Reset page when search/tab changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, activeTab]);

  // Refresh function
  const handleRefresh = async () => {
    setCurrentPage(1);
    setSearchTerm('');
    setActiveTab('my');
    setIsAddModalOpen(false);
    setIsEditModalOpen(false);
    setSelectedCandidate(null);
    
    await queryClient.invalidateQueries({ queryKey: ["candidates"] });
    await queryClient.invalidateQueries({ queryKey: ["folders-filter"] });
    
    toast({
      title: "Refreshed",
      description: "Data has been refreshed successfully",
    });
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleTabChange = (newTab) => {
    setActiveTab(newTab);
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePaginationToggle = (enabled) => {
    setIsPaginationEnabled(enabled);
    if (!enabled) {
      setCurrentPage(1);
    }
  };

  const handleRowsPerPageChange = (value) => {
    setRowsPerPage(parseInt(value));
    setCurrentPage(1);
  };

  const handleDeleteCandidate = async (id) => {
    setCandidateToDelete(id);
    setIsDeleteDialogOpen(true);
  };

  const confirmDeleteCandidate = async () => {
    if (!candidateToDelete) return;
    
    try {
      await candidatesAPI2.delete(candidateToDelete);
      toast({
        title: "Candidate deleted",
        description: "The candidate has been deleted successfully",
      });
      refetchCandidates();
      refetchFolders();
    } catch (error) {
      toast({
        title: "Error",
        description:
          error.response?.data?.message || "Failed to delete candidate",
        variant: "destructive",
      });
    } finally {
      setIsDeleteDialogOpen(false);
      setCandidateToDelete(null);
    }
  };

  const handleEditCandidate = (candidate) => {
    setSelectedCandidate(candidate);
    setIsEditModalOpen(true);
  };

  const handleCreateFolder = (refetchCallback) => {
    setEditingFolder(null);
    setFolderModalRefetchCallback(() => refetchCallback);
    setIsFolderModalOpen(true);
  };

  const handleEditFolder = (folder, refetchCallback) => {
    setEditingFolder(folder);
    setFolderModalRefetchCallback(() => refetchCallback);
    setIsFolderModalOpen(true);
  };

  const handleAddCandidateClick = async () => {
    try {
      const response = await settingsAPI.getCloudStorageSettings();
      if (response.data.success) {
        const isConfigured = response.data.data.bucket && 
                           response.data.data.access_key && 
                           response.data.data.secret_key;
      }
    } catch (error) {
      console.error("Failed to fetch cloud storage settings:", error);
    }
    
    setIsAddModalOpen(true);
  };

  // Handle viewing candidates in a folder
  const handleViewCandidates = async (folder) => {
    setSelectedFolder(folder);
    setCandidateSearchTerm("");
    setCandidatePage(1);
    setIsLoadingFolderCandidates(true);
    setIsViewCandidatesOpen(true);

    try {
      const response = await candidatesAPI2.getMyFolderCandidates({
        folderId: folder._id || folder.id,
        page: 1,
        limit: PAGE_LIMIT,
        isMyCandidate: activeTab === "my",
      });

      const candidates = response.data?.data?.candidates || [];
      const pagination = response.data?.data?.pagination || {
        totalPages: 1,
        currentPage: 1,
        totalItems: 0,
      };

      setFolderCandidates(candidates);
      setFolderCandidatesPagination({
        totalPages: pagination.pages || pagination.totalPages || 1,
        currentPage: pagination.page || 1,
        totalItems: pagination.total || 0,
      });
    } catch (error) {
      console.error("Error fetching folder candidates:", error);
      toast({
        title: "Error",
        description: "Failed to load folder candidates",
        variant: "destructive",
      });
    } finally {
      setIsLoadingFolderCandidates(false);
    }
  };

  // Handle search within folder candidates
  const handleSearchFolderCandidates = async (searchValue) => {
    if (!selectedFolder) return;

    setIsLoadingFolderCandidates(true);
    try {
      const response = await candidatesAPI2.getMyFolderCandidates({
        folderId: selectedFolder._id || selectedFolder.id,
        page: 1,
        limit: PAGE_LIMIT,
        search: searchValue,
        isMyCandidate: activeTab === "my",
      });

      const candidates = response.data?.data?.candidates || [];
      const pagination = response.data?.data?.pagination || {
        totalPages: 1,
        currentPage: 1,
        totalItems: 0,
      };

      setFolderCandidates(candidates);
      setFolderCandidatesPagination({
        totalPages: pagination.pages || pagination.totalPages || 1,
        currentPage: 1,
        totalItems: pagination.total || pagination.totalItems || 0,
      });
      setCandidatePage(1);
    } catch (error) {
      console.error("Error searching folder candidates:", error);
      toast({
        title: "Error",
        description: "Failed to search candidates",
        variant: "destructive",
      });
    } finally {
      setIsLoadingFolderCandidates(false);
    }
  };

  // Handle pagination within folder candidates
  const handleFolderCandidatePageChange = async (newPage) => {
    if (!selectedFolder) return;

    setIsLoadingFolderCandidates(true);
    try {
      const response = await candidatesAPI2.getMyFolderCandidates({
        folderId: selectedFolder._id || selectedFolder.id,
        page: newPage,
        limit: PAGE_LIMIT,
        search: candidateSearchTerm,
        isMyCandidate: activeTab === "my",
      });

      const candidates = response.data?.data?.candidates || [];
      const pagination = response.data?.data?.pagination || {
        totalPages: 1,
        currentPage: newPage,
        totalItems: 0,
      };

      setFolderCandidates(candidates);
      setFolderCandidatesPagination({
        totalPages: pagination.pages || pagination.totalPages || 1,
        currentPage: newPage,
        totalItems: pagination.total || pagination.totalItems || 0,
      });
      setCandidatePage(newPage);
    } catch (error) {
      console.error("Error changing folder candidates page:", error);
      toast({
        title: "Error",
        description: "Failed to load page",
        variant: "destructive",
      });
    } finally {
      setIsLoadingFolderCandidates(false);
    }
  };

  // Handle delete candidate from folder view
  const handleDeleteCandidateFromFolder = async (candidateId) => {
    setCandidateToDelete(candidateId);
    setIsDeleteDialogOpen(true);
  };

  const confirmDeleteCandidateFromFolder = async () => {
    if (!candidateToDelete) return;

    try {
      await candidatesAPI2.delete(candidateToDelete);

      toast({
        title: "Success",
        description: "Candidate deleted successfully",
        variant: "default",
      });

      refetchCandidates();
      refetchFolders();

      // Refresh candidates in the folder dialog
      const response = await candidatesAPI2.getMyFolderCandidates({
        folderId: selectedFolder._id || selectedFolder.id,
        page: candidatePage,
        limit: PAGE_LIMIT,
        search: candidateSearchTerm,
        isMyCandidate: activeTab === "my",
      });

      const candidates = response.data?.data?.candidates || [];
      const pagination = response.data?.data?.pagination || {
        totalPages: 1,
        currentPage: candidatePage,
        totalItems: 0,
      };

      setFolderCandidates(candidates);
      setFolderCandidatesPagination(pagination);

      if (candidates.length === 0) {
        setIsViewCandidatesOpen(false);
        setIsDeleteDialogOpen(false);
        setCandidateToDelete(null);
        return;
      }

      if (candidates.length === 0 && candidatePage > 1) {
        handleFolderCandidatePageChange(candidatePage - 1);
      }
    } catch (error) {
      console.error("Error deleting candidate:", error);
      toast({
        title: "Error",
        description:
          error.response?.data?.message || "Failed to delete candidate",
        variant: "destructive",
      });
    } finally {
      setIsDeleteDialogOpen(false);
      setCandidateToDelete(null);
    }
  };

  // Render folders in table format
  const renderFoldersTable = () => {
    const folders = candidatesData?.data || [];
    const pagination = candidatesData?.pagination || {
      totalPages: 1,
      currentPage: 1,
      totalItems: 0,
    };

    return (
      <>
        {candidatesLoading ? (
          <ModernLoading text="Loading folders..." />
        ) : candidatesError ? (
          <div className="text-center p-6 text-red-500">
            Error loading folders. Please try again.
          </div>
        ) : folders.length > 0 ? (
          <>
            <div className="admin-table-container">
              <table className="admin-table">
                <thead className="admin-table-header">
                  <tr>
                    <th className="admin-table-cell">S.No</th>
                    <th className="admin-table-cell">Folder Name</th>
                    <th className="admin-table-cell">Description</th>
                    <th className="admin-table-cell">Total Candidates</th>
                    <th className="admin-table-cell">Created By</th>
                    <th className="admin-table-cell">Created At</th>
                    <th className="admin-table-cell text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {folders.map((folder, index) => (
                    <tr key={folder._id || folder.id} className="admin-table-row">
                      <td className="admin-table-cell number">
                        {isPaginationEnabled ? (currentPage - 1) * rowsPerPage + index + 1 : index + 1}
                      </td>
                      <td className="admin-table-cell font-medium">
                        <div className="flex items-center">
                          <Folder
                            className="w-4 h-4 mr-2 flex-shrink-0"
                            style={{ color: folder.color || "#3882F6" }}
                          />
                          <span className="text-sm">{folder.name || "Unnamed Folder"}</span>
                        </div>
                      </td>
                      <td className="admin-table-cell">
                        <span className="text-sm text-gray-600">
                          {folder.description || "No description"}
                        </span>
                      </td>
                      <td className="admin-table-cell">
                        <ModernBadge variant="language">
                          <span className="text-xs">
                            {folder.totalCandidates || folder.candidates?.length || 0} candidate
                            {(folder.totalCandidates !== 1 && folder.candidates?.length !== 1) && "s"}
                          </span>
                        </ModernBadge>
                      </td>
                      <td className="admin-table-cell">
                        {folder.createdBy ? (
                          typeof folder.createdBy === 'object' && folder.createdBy.name ? (
                            <ModernBadge variant="template">
                              <span className="text-xs">{folder.createdBy.name}</span>
                            </ModernBadge>
                          ) : (
                            <span className="text-gray-400 text-xs">
                              {typeof folder.createdBy === 'string' ? 'User' : 'N/A'}
                            </span>
                          )
                        ) : (
                          <span className="text-gray-400 text-xs">N/A</span>
                        )}
                      </td>
                      <td className="admin-table-cell">
                        <span className="text-xs">
                          {folder.createdAt ? format(new Date(folder.createdAt), "MMM dd, yyyy") : "N/A"}
                        </span>
                      </td>
                      <td className="admin-table-cell text-right">
                        <div className="admin-table-actions">
                          {(folder.totalCandidates || folder.candidates?.length) > 0 && (
                            <button
                              className="admin-table-action-btn view"
                              onClick={() => handleViewCandidates(folder)}
                              title="View candidates"
                            >
                              <Eye className="h-3 w-3" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        ) : (
          <div className="text-center p-6 text-gray-500">
            No folders found
          </div>
        )}
      </>
    );
  };

  return (
    <ModernPageTemplate
      title="Candidates Management"
      contentClassName="candidates-page"
      stats={{
        total: candidatesData?.pagination?.totalItems || candidatesData?.totalCandidates || 0,
        customStats: (
          <Tabs value={activeTab} onValueChange={handleTabChange}>
            <TabsList className="categories-header-tabs-list">
              <TabsTrigger value="my" className="categories-header-tab">
                <User className="h-3 w-3" />
                My Candidates
              </TabsTrigger>
              <TabsTrigger value="company" className="categories-header-tab">
                <Users className="h-3 w-3" />
                Company Candidates
              </TabsTrigger>
            </TabsList>
          </Tabs>
        )
      }}
      onRefresh={handleRefresh}
      search={{
        placeholder: "Search folders by name...",
        value: searchTerm,
        onChange: handleSearchChange,
        onSubmit: (e) => e.preventDefault()
      }}
      headerAction={
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setIsFolderManagementOpen(true)}
            title="Manage Folders"
          >
            <Settings className="w-4 h-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleAddCandidateClick}
            title="Add New Candidate"
          >
            <UserPlus className="w-4 h-4" />
          </Button>
        </div>
      }
    >
      {renderFoldersTable()}

      {/* Modern Pagination Footer */}
      <ModernPaginationFooter
        isPaginationEnabled={isPaginationEnabled}
        onPaginationToggle={handlePaginationToggle}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleRowsPerPageChange}
        pagination={{
          current: currentPage,
          pages: candidatesData?.pagination?.totalPages || 1,
          total: candidatesData?.pagination?.totalItems || 0
        }}
        onPageChange={handlePageChange}
        rowsOptions={[5, 10, 20, 50]}
      />

      {/* View Candidates Dialog */}
      <Dialog open={isViewCandidatesOpen} onOpenChange={setIsViewCandidatesOpen}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>
              {selectedFolder?.name || "Folder"} - Candidates
            </DialogTitle>
          </DialogHeader>

          <div className="flex justify-between items-center mb-4">
            <div className="relative flex-1">
              <Input
                placeholder="Search candidates..."
                value={candidateSearchTerm}
                onChange={(e) => {
                  setCandidateSearchTerm(e.target.value);
                  handleSearchFolderCandidates(e.target.value);
                }}
                className="w-full"
              />
            </div>
          </div>

          <div className="overflow-x-auto">
            {isLoadingFolderCandidates ? (
              <ModernLoading text="Loading candidates..." />
            ) : folderCandidates.length > 0 ? (
              <>
                <div className="admin-table-container">
                  <table className="admin-table">
                    <thead className="admin-table-header">
                      <tr>
                        <th className="admin-table-cell">S.No</th>
                        <th className="admin-table-cell">Name</th>
                        <th className="admin-table-cell">Email</th>
                        <th className="admin-table-cell">Mobile</th>
                        <th className="admin-table-cell">Created At</th>
                        <th className="admin-table-cell text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {folderCandidates.map((candidate, index) => (
                        <tr key={candidate._id || index} className="admin-table-row">
                          <td className="admin-table-cell number">
                            {(candidatePage - 1) * PAGE_LIMIT + index + 1}
                          </td>
                          <td className="admin-table-cell font-medium">
                            <div className="flex items-center">
                              <User className="w-3 h-3 mr-1 text-gray-400 flex-shrink-0" />
                              <span className="text-sm">{candidate.name || "N/A"}</span>
                            </div>
                          </td>
                          <td className="admin-table-cell">
                            <span className="text-sm">{candidate.email || "N/A"}</span>
                          </td>
                          <td className="admin-table-cell">
                            <span className="text-sm">{candidate.mobileNumber || "N/A"}</span>
                          </td>
                          <td className="admin-table-cell">
                            <span className="text-xs">
                              {format(new Date(candidate.createdAt), "MMM dd, yyyy")}
                            </span>
                          </td>
                          <td className="admin-table-cell text-right">
                            <div className="admin-table-actions">
                              <button
                                className="admin-table-action-btn edit"
                                onClick={() => {
                                  setSelectedCandidate(candidate);
                                  setIsEditModalOpen(true);
                                }}
                                title="Edit candidate"
                              >
                                <Pencil className="h-3 w-3" />
                              </button>
                              <button
                                className="admin-table-action-btn delete"
                                onClick={() => handleDeleteCandidateFromFolder(candidate._id || candidate.id)}
                                title="Delete candidate"
                              >
                                <Trash className="h-3 w-3" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {folderCandidatesPagination.totalPages > 1 && (
                  <div className="flex justify-center mt-4">
                    <Pagination>
                      <PaginationContent>
                        <PaginationItem>
                          <PaginationPrevious
                            onClick={() => handleFolderCandidatePageChange(candidatePage - 1)}
                            disabled={candidatePage === 1}
                            className={
                              candidatePage === 1
                                ? "pointer-events-none opacity-50"
                                : "cursor-pointer"
                            }
                          />
                        </PaginationItem>
                        {Array.from(
                          { length: folderCandidatesPagination.totalPages },
                          (_, i) => i + 1
                        ).map((page) => (
                          <PaginationItem key={page}>
                            <PaginationLink
                              isActive={candidatePage === page}
                              onClick={() => handleFolderCandidatePageChange(page)}
                              className={`cursor-pointer ${
                                candidatePage === page
                                  ? "bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground"
                                  : ""
                              }`}
                            >
                              {page}
                            </PaginationLink>
                          </PaginationItem>
                        ))}
                        <PaginationItem>
                          <PaginationNext
                            onClick={() => handleFolderCandidatePageChange(candidatePage + 1)}
                            disabled={candidatePage === folderCandidatesPagination.totalPages}
                            className={
                              candidatePage === folderCandidatesPagination.totalPages
                                ? "pointer-events-none opacity-50"
                                : "cursor-pointer"
                            }
                          />
                        </PaginationItem>
                      </PaginationContent>
                    </Pagination>
                  </div>
                )}
              </>
            ) : (
              <p className="text-center text-gray-500">
                {candidateSearchTerm
                  ? "No candidates match your search."
                  : "No candidates found in this folder."}
              </p>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Modals */}
      <AddCandidateModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onSuccess={() => {
          setCurrentPage(1);
          refetchCandidates();
          refetchFolders();
          setIsAddModalOpen(false);
          toast({
            title: "Candidate created",
            description: "The candidate has been created successfully",
          });
        }}
      />

      <EditCandidateModal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setSelectedCandidate(null);
        }}
        candidateId={selectedCandidate?._id || selectedCandidate?.id}
        onSuccess={() => {
          refetchCandidates();
          refetchFolders();
          setIsEditModalOpen(false);
          toast({
            title: "Candidate updated",
            description: "The candidate has been updated successfully",
          });
        }}
      />

      <CreateFolderModal
        isOpen={isFolderModalOpen}
        onClose={() => setIsFolderModalOpen(false)}
        onSuccess={() => {
          refetchCandidates();
          refetchFolders();
          setIsFolderModalOpen(false);
          if (folderModalRefetchCallback) {
            folderModalRefetchCallback();
          }
          toast({
            title: "Folder saved",
            description: "The folder has been saved successfully",
          });
        }}
        editFolder={editingFolder}
      />

      <FolderManagementModal
        isOpen={isFolderManagementOpen}
        onClose={() => setIsFolderManagementOpen(false)}
        onCreateFolder={handleCreateFolder}
        onEditFolder={handleEditFolder}
        onFolderCreatedOrEdited={() => {
          refetchCandidates();
          refetchFolders();
        }}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the candidate
              and remove their data from the system.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => {
              setIsDeleteDialogOpen(false);
              setCandidateToDelete(null);
            }}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={isViewCandidatesOpen ? confirmDeleteCandidateFromFolder : confirmDeleteCandidate}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ModernPageTemplate>
  );
};

export default Candidates;